<template>
  <div class="d-flex flex-column min-vh-100">
    <PriorHeader />

    <v-container class="flex-grow-1 d-flex">
      <v-row justify="center" class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center mb-12 mt-16">
            <h1 class="text-primary weight-600">Login</h1>
          </div>

          <v-card class="pa-6">
            <v-card-text>
              <div v-if="!secondVerificationStep">
                <v-form @submit.prevent="loginUser">
                  <v-text-field
                    v-model="credentials.email"
                    label="Email Address"
                    type="email"
                    placeholder="example@findsunrise.com"
                    :error-messages="!isEmailValid && formButtonClicked ? 'Please enter a valid email address' : ''"
                    class="mb-4 lh--force-white-background" />

                  <v-text-field
                    v-model="credentials.password"
                    label="Password"
                    :type="showPassword ? 'text' : 'password'"
                    :error-messages="getPasswordErrorMessage"
                    class="mb-4 lh--force-white-background"
                    :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append-inner="showPassword = !showPassword" />

                  <v-btn color="primary" block size="large" type="submit" class="mb-4" :loading="showPreloader">
                    Login
                  </v-btn>

                  <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mb-4">
                    <div v-for="error in errors" :key="error">{{ error }}</div>
                  </v-alert>

                  <div class="d-flex flex-column align-center mt-6">
                    <router-link
                      :to="{ name: 'password-reset-request' }"
                      class="text-decoration-none mb-2 text-primary weight-500">
                      Forgot Password?
                    </router-link>
                    <!--                    <div class="text-center text-color-blue-gray weight-500">-->
                    <!--                      Don't have an account?-->
                    <!--                      <router-link :to="{ name: 'register' }" class="text-decoration-none text-primary weight-500">-->
                    <!--                        Create an account-->
                    <!--                      </router-link>-->
                    <!--                    </div>-->
                  </div>
                </v-form>
              </div>

              <div v-else>
                <v-form @submit.prevent="verifyOtp">
                  <p class="text-center mb-4 text-primary weight-500 lh--force-white-background">
                    Check your email, enter One Time Password (OTP):
                  </p>
                  <v-otp-input
                    v-model="otpCode"
                    class="mb-5 mt-8 lh--force-white-background"
                    length="6"
                    color="surface"
                    :disabled="showPreloader" />

                  <v-btn
                    color="primary"
                    block
                    size="large"
                    type="submit"
                    class="mb-4"
                    :loading="showPreloader"
                    :disabled="!otpCode || otpCode.length !== 6">
                    Verify OTP
                  </v-btn>

                  <v-alert v-if="errors.length > 0" type="error" variant="tonal">
                    <div v-for="error in errors" :key="error">{{ error }}</div>
                  </v-alert>
                </v-form>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <PriorFooter />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import PriorHeader from "@/components/PriorHeader";
import PriorFooter from "@/components/PriorFooter";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";

const secondVerificationStep = ref(false);
const otpCode = ref("");
const errors = ref([]);
const formButtonClicked = ref(false);
const router = useRouter();
const authStore = useAuthStore();
const showPreloader = ref(false);
const showPassword = ref(false);

// Add watcher for otpCode
watch(otpCode, (newValue) => {
  // Automatically submit when OTP is fully entered (6 digits)
  if (newValue && newValue.length === 6) {
    verifyOtp();
  }
});

const credentials = ref({
  email: "",
  password: "",
});

// Validators
const isEmailValid = computed(() => {
  const email = credentials.value.email;
  return email !== "" && FORM_VALIDATION_PATTERNS.EMAIL_PATTERN.test(email);
});

const isPasswordValid = computed(() => {
  const password = credentials.value.password;
  return FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password);
});

const getPasswordErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!credentials.value.password) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const loginUser = async () => {
  formButtonClicked.value = true;
  if (isEmailValid.value && isPasswordValid.value) {
    showPreloader.value = true;
    try {
      let otpConfig = await authStore.login(credentials.value);
      if (otpConfig.secondStep) {
        secondVerificationStep.value = true;
      } else {
        await authStore.loginStep2NotRequired();
        router.push({ name: "all-submissions" });
      }
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    }
    showPreloader.value = false;
  }
};

const verifyOtp = async () => {
  if (!otpCode.value || otpCode.value.length !== 6) return;

  showPreloader.value = true;
  try {
    await authStore.loginStep2(otpCode.value);
    router.push({ name: "all-submissions" });
    secondVerificationStep.value = false;
    otpCode.value = ""; // Clear OTP after successful verification
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    showPreloader.value = false;
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";
@import "../styles/pages/_authorisation-forms.scss";
</style>
